/**
 * pickAcademicSignals — one canonical extractor for the three A-Level
 * signals we surface across the app:
 *
 *   1. **Advertised**  — the offer the university PROMISES.
 *      Source of truth: `degree.entry_requirements.a_level.typical`
 *      with `degree.entry_requirements.a_level.evidence.url` for the
 *      citation. Written by the LLM-extract pipeline (step 2) gated
 *      on `>= uni_min`.
 *
 *   2. **Intake**      — what students who ENROLLED last cycle scored.
 *      Source: `degree.hesa_stats.typical_a_level` (derived from KIS
 *      TARIFF.csv). Distinct from the offer: contextual admits soften
 *      it, over-qualified applicants harden it.
 *
 *   3. **Uni-wide**    — the university-family typical/minimum offer,
 *      used as a family-level floor when we have no course-level data.
 *      Source: `university.curricula.a_level.typical|minimum`.
 *
 * Rendering guidance:
 *   - Never let one signal silently overwrite another — the whole point
 *     of the 2026-02 purge was to stop conflating course-advertised
 *     with uni-wide-advertised.
 *   - When a caller must collapse to ONE value (e.g. the "Academic
 *     fit" conclusion tile), prefer `advertised → intake → uniWide`.
 *     Label the source clearly ("A-Level typical · advertised" vs
 *     "A-Level typical intake · HESA").
 */

const _isMeaningful = (v) =>
  v != null && String(v).trim() !== "" && String(v).trim().toLowerCase() !== "pass";

// Countries where the UK A-Level letter-grade / UCAS tariff idiom is
// the native admissions currency. For everyone else, an A-Level row on
// the degree is a *courtesy signal for British applicants* — genuinely
// useful, but should never headline the "Academic fit" tile of a US /
// Dutch / Australian degree, or a US student sees "A*A*A*" on MIT and
// wonders what year the school moved to Cambridge.
const _UK_ALEVEL_COUNTRIES = new Set([
  "UK", "United Kingdom", "GB", "England", "Scotland", "Wales", "Northern Ireland",
  "Ireland", "IE",
]);

export function pickAcademicSignals(uni, degree = {}) {
  const er = degree.entry_requirements || {};
  const hs = degree.hesa_stats || {};
  const admissions = degree.admissions || {};
  const curricula = uni?.curricula || {};

  // Portfolio / audition-led courses don't publish grade offers —
  // callers surface the UCAS tariff floor or the portfolio note.
  // This signal IS country-neutral (US art schools are portfolio-only
  // too), so it stays regardless of country gate.
  const entry_gate = admissions.entry_gate || null;

  // Country gate — for non-UK/IE degrees, none of the A-Level or UCAS
  // tariff rows should headline. We still expose `entry_gate` so
  // portfolio/audition US degrees render correctly, and callers who
  // want the UK-courtesy signal can read `degree.entry_requirements
  // .a_level` directly.
  const country = uni?.country || null;
  if (country && !_UK_ALEVEL_COUNTRIES.has(country)) {
    return { advertised: null, intake: null, uniWide: null, entry_gate, ucasTariff: null };
  }

  // UCAS tariff-points offer path — 40 courses in our data (art,
  // music, drama). NOT a substitute for A-Level letters; a distinct
  // representation. Callers usually branch on this before showing
  // letter-grade rows.
  const tariff = er.ucas_tariff;
  const ucasTariff =
    tariff?.standard != null || tariff?.contextual != null
      ? { standard: tariff.standard ?? null, contextual: tariff.contextual ?? null }
      : null;

  const advertised = _isMeaningful(er?.a_level?.typical)
    ? {
        value: er.a_level.typical,
        minimum: _isMeaningful(er.a_level.minimum) ? er.a_level.minimum : null,
        // evidence.url points at the provider admissions page we
        // scraped this from. Present iff the LLM-extract pipeline
        // wrote this row (step 2). Older seed rows lack it.
        evidence: er.a_level.evidence || null,
      }
    : null;

  const intake = _isMeaningful(hs?.typical_a_level)
    ? {
        value: hs.typical_a_level,
        // HESA doesn't publish a per-course sample_size for typical
        // A-Level directly; the tariff distribution's sample is the
        // closest analogue. Callers use it to badge low-N cohorts.
        sample_size: hs.tariff_sample_size ?? null,
        academic_year: hs.academic_year || null,
      }
    : null;

  const uniWide =
    _isMeaningful(curricula?.a_level?.typical) || _isMeaningful(curricula?.a_level?.minimum)
      ? {
          value: _isMeaningful(curricula?.a_level?.typical) ? curricula.a_level.typical : null,
          minimum: _isMeaningful(curricula?.a_level?.minimum) ? curricula.a_level.minimum : null,
        }
      : null;

  return { advertised, intake, uniWide, entry_gate, ucasTariff };
}

/**
 * primaryDisplayValue — collapse the three signals to one string when
 * the caller has no room for the full breakdown (e.g. shortlist card,
 * "Academic fit" conclusion tile). Preserves the SOURCE label so we
 * never lie about which signal we're showing.
 */
export function primaryDisplayValue(signals) {
  if (!signals) return { value: "—", system: null, kind: null };
  const { advertised, intake, uniWide, ucasTariff, entry_gate } = signals;
  if (entry_gate === "portfolio" || entry_gate === "audition") {
    return { value: "Portfolio-led", system: "Portfolio / audition", kind: "portfolio" };
  }
  if (ucasTariff?.standard != null) {
    const label = ucasTariff.contextual != null
      ? `${ucasTariff.standard} pts (contextual ${ucasTariff.contextual})`
      : `${ucasTariff.standard} pts`;
    return { value: label, system: "UCAS tariff · standard offer", kind: "tariff" };
  }
  if (advertised) {
    return { value: advertised.value, system: "A-Level · advertised offer", kind: "advertised" };
  }
  if (intake) {
    return { value: intake.value, system: "A-Level · typical intake (HESA)", kind: "intake" };
  }
  if (uniWide?.value) {
    return { value: uniWide.value, system: "A-Level · uni-wide typical", kind: "uni_wide" };
  }
  return { value: "—", system: null, kind: null };
}
